import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Layout } from "@/pages/Dashboard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { MapPin, Car, Train, Clock, Save, Loader2, Calendar, Link2, Unlink, RefreshCw, CheckCircle2 } from "lucide-react";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { StationAutocomplete } from "@/components/StationAutocomplete";
import { RegularPlacesEditor } from "@/components/RegularPlacesEditor";

export const TravelSettings = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  const [formData, setFormData] = useState({
    home_address: "",
    home_postcode: "",
    home_city: "",
    home_country: "UK",
    home_station: "",
    home_station_name: "",
    home_station_place_id: "",
    home_station_lat: null,
    home_station_lng: null,
    default_transport_mode: "driving",
    buffer_minutes: 30,
    apply_buffer_in_dashboard: true,
    mileage_expense_format: "amount",
    regular_places: []
  });

  const [calendarStatus, setCalendarStatus] = useState({
    connected: false,
    auto_sync_enabled: true,
    sync_public_only: false,
    last_sync: null
  });

  useEffect(() => {
    fetchSettings();
    fetchCalendarStatus();
    
    // Check if redirected from OAuth
    if (searchParams.get("calendar_connected") === "true") {
      toast.success("Google Calendar connected!");
      fetchCalendarStatus();
    }
  }, [searchParams]);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get("/user/travel-settings");
      setFormData({
        home_address: response.data.home_address || "",
        home_postcode: response.data.home_postcode || "",
        home_city: response.data.home_city || "",
        home_country: response.data.home_country || "UK",
        home_station: response.data.home_station || "",
        home_station_name: response.data.home_station_name || response.data.home_station || "",
        home_station_place_id: response.data.home_station_place_id || "",
        home_station_lat: response.data.home_station_lat ?? null,
        home_station_lng: response.data.home_station_lng ?? null,
        default_transport_mode: response.data.default_transport_mode || "driving",
        buffer_minutes: response.data.buffer_minutes || 30,
        apply_buffer_in_dashboard: response.data.apply_buffer_in_dashboard ?? true,
        mileage_expense_format: response.data.mileage_expense_format || "amount",
        regular_places: response.data.regular_places || []
      });
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarStatus = async () => {
    try {
      const response = await apiClient.get("/calendar/status");
      setCalendarStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch calendar status:", error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await apiClient.put("/user/travel-settings", formData);
      // The backend fills in home → place miles for newly added regular places.
      setFormData((prev) => ({ ...prev, regular_places: response.data?.regular_places || prev.regular_places }));
      toast.success("Travel settings saved");
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || "Unknown error";
      console.error("Failed to save travel settings:", msg, error);
      toast.error(`Failed to save settings: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const connectCalendar = async () => {
    try {
      const response = await apiClient.get("/calendar/auth-url");
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error("Failed to initiate calendar connection");
    }
  };

  const disconnectCalendar = async () => {
    if (!confirm("Disconnect Google Calendar? Your gigs will stop syncing.")) return;
    
    try {
      await apiClient.post("/calendar/disconnect");
      setCalendarStatus({ ...calendarStatus, connected: false });
      toast.success("Calendar disconnected");
    } catch (error) {
      toast.error("Failed to disconnect calendar");
    }
  };

  const syncAllGigs = async () => {
    setSyncing(true);
    try {
      const response = await apiClient.post("/calendar/sync-all");
      toast.success(`Synced ${response.data.synced_count} of ${response.data.total_gigs} gigs`);
      fetchCalendarStatus();
    } catch (error) {
      toast.error("Failed to sync gigs");
    } finally {
      setSyncing(false);
    }
  };

  const updateCalendarSettings = async (key, value) => {
    try {
      await apiClient.put(`/calendar/settings?${key}=${value}`);
      setCalendarStatus({ ...calendarStatus, [key]: value });
      toast.success("Calendar settings updated");
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="settings-page">
        {/* Header */}
        <div className="mb-8">
          <p className="font-mono text-xs uppercase tracking-widest text-primary mb-2">Settings</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-2">
            Configure travel, calendar sync, and other preferences
          </p>
        </div>

        <div className="max-w-2xl space-y-6">
          
          {/* Google Calendar Sync */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Google Calendar Sync
              </CardTitle>
              <CardDescription>
                Automatically sync your gigs to Google Calendar
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {calendarStatus.connected ? (
                <>
                  <div className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                      <div>
                        <p className="font-medium text-emerald-400">Connected</p>
                        {calendarStatus.last_sync && (
                          <p className="text-xs text-muted-foreground">
                            Last synced: {new Date(calendarStatus.last_sync).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={disconnectCalendar}
                      className="text-red-400 border-red-400/50 hover:bg-red-400/10"
                      data-testid="disconnect-calendar"
                    >
                      <Unlink className="h-4 w-4 mr-2" />
                      Disconnect
                    </Button>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Auto-sync new gigs</Label>
                        <p className="text-xs text-muted-foreground">
                          Automatically add gigs to your calendar when created
                        </p>
                      </div>
                      <Switch
                        checked={calendarStatus.auto_sync_enabled}
                        onCheckedChange={(v) => updateCalendarSettings("auto_sync_enabled", v)}
                        data-testid="auto-sync-toggle"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Only sync public gigs</Label>
                        <p className="text-xs text-muted-foreground">
                          Private gigs won't appear in your calendar
                        </p>
                      </div>
                      <Switch
                        checked={calendarStatus.sync_public_only}
                        onCheckedChange={(v) => updateCalendarSettings("sync_public_only", v)}
                        data-testid="public-only-toggle"
                      />
                    </div>
                  </div>

                  <Button 
                    onClick={syncAllGigs}
                    disabled={syncing}
                    variant="secondary"
                    className="w-full"
                    data-testid="sync-all-btn"
                  >
                    {syncing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Sync All Upcoming Gigs Now
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <div className="text-center py-4">
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">
                    Connect your Google Calendar to automatically sync your gigs
                  </p>
                  <Button 
                    onClick={connectCalendar}
                    className="bg-primary text-primary-foreground"
                    data-testid="connect-calendar"
                  >
                    <Link2 className="h-4 w-4 mr-2" />
                    Connect Google Calendar
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Home Address */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                Home Address
              </CardTitle>
              <CardDescription>
                Your starting point for journey calculations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="home_address">Street Address</Label>
                <Input
                  id="home_address"
                  value={formData.home_address}
                  onChange={(e) => setFormData({ ...formData, home_address: e.target.value })}
                  data-testid="home-address"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="home_city">City</Label>
                  <Input
                    id="home_city"
                    value={formData.home_city}
                    onChange={(e) => setFormData({ ...formData, home_city: e.target.value })}
                    data-testid="home-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="home_postcode">Postcode</Label>
                  <Input
                    id="home_postcode"
                    value={formData.home_postcode}
                    onChange={(e) => setFormData({ ...formData, home_postcode: e.target.value })}
                    data-testid="home-postcode"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="home_country">Country</Label>
                  <Input
                    id="home_country"
                    value={formData.home_country}
                    onChange={(e) => setFormData({ ...formData, home_country: e.target.value })}
                    data-testid="home-country"
                  />
                </div>
              </div>

              <div className="space-y-2 pt-2 border-t border-border">
                <Label className="flex items-center gap-2">
                  <Train className="h-4 w-4 text-primary" />
                  Nearest Railway Station
                </Label>
                <StationAutocomplete
                  value={
                    formData.home_station_name
                      ? {
                          name: formData.home_station_name,
                          place_id: formData.home_station_place_id,
                          lat: formData.home_station_lat,
                          lng: formData.home_station_lng,
                        }
                      : null
                  }
                  onChange={(next) => setFormData({
                    ...formData,
                    home_station_name: next?.name || "",
                    home_station_place_id: next?.place_id || "",
                    home_station_lat: next?.lat ?? null,
                    home_station_lng: next?.lng ?? null,
                    home_station: next?.name || "",
                  })}
                  placeholder="e.g. Reading"
                  testId="home-station"
                />
                <p className="text-xs text-muted-foreground">
                  When your default transport is Public Transport, journeys are calculated
                  as drive to this station + the actual next train departure. Leave blank
                  to use end-to-end public transport from home.
                </p>
              </div>

              <div className="space-y-2 pt-2 border-t border-border">
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  Regular mileage
                </Label>
                <p className="text-xs text-muted-foreground">
                  Places you drive to that aren't the gig itself, like the station you park at.
                  Pick them in a gig's Transport section to log home ↔ place mileage.
                </p>
                <RegularPlacesEditor
                  places={formData.regular_places}
                  onChange={(regular_places) => setFormData((prev) => ({ ...prev, regular_places }))}
                />
              </div>
            </CardContent>
          </Card>

          {/* Travel Preferences */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Car className="h-5 w-5 text-primary" />
                Travel Preferences
              </CardTitle>
              <CardDescription>
                Default settings for journey calculations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Default Transport Mode</Label>
                  <Select 
                    value={formData.default_transport_mode} 
                    onValueChange={(v) => setFormData({ ...formData, default_transport_mode: v })}
                  >
                    <SelectTrigger data-testid="transport-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="driving">
                        <span className="flex items-center gap-2">
                          <Car className="h-4 w-4" />
                          Driving
                        </span>
                      </SelectItem>
                      <SelectItem value="transit">
                        <span className="flex items-center gap-2">
                          <Train className="h-4 w-4" />
                          Public Transport
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="buffer">Buffer Time (minutes)</Label>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <Input
                      id="buffer"
                      type="number"
                      value={formData.buffer_minutes}
                      onChange={(e) => setFormData({ ...formData, buffer_minutes: parseInt(e.target.value) || 0 })}
                      min="0"
                      max="120"
                      className="w-24"
                      data-testid="buffer-time"
                    />
                    <span className="text-sm text-muted-foreground">min</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Extra time added for parking, setup, etc.
                  </p>
                </div>
              </div>

              {/* Mileage expense format */}
              <div className="space-y-2 pt-2 border-t border-border">
                <Label>Mileage expense format</Label>
                <Select
                  value={formData.mileage_expense_format}
                  onValueChange={(value) => setFormData({ ...formData, mileage_expense_format: value })}
                  data-testid="mileage-expense-format"
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="amount">
                      <div className="flex flex-col items-start">
                        <span className="font-medium">£ Amount (HMRC rate × miles)</span>
                        <span className="text-xs text-muted-foreground">Add the calculated £ value to expenses — for most apps</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="miles">
                      <div className="flex flex-col items-start">
                        <span className="font-medium">Miles only</span>
                        <span className="text-xs text-muted-foreground">Add raw miles — for accounting software that handles HMRC rate itself (e.g. FreeAgent)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Controls what gets added to gig expenses when you use the mileage calculator.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-primary text-primary-foreground"
              data-testid="save-settings"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TravelSettings;
